import { useState } from "react";
import { useApi } from "@/lib/useApi";
import { api, apiError } from "@/lib/api";
import { useRefresh } from "@/context/RefreshContext";
import { PageTitle, Panel, Loading, Empty, ErrorState } from "@/components/Common";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Plus, Pencil, Archive, Server } from "lucide-react";
import { toast } from "sonner";

export default function Groups() {
  const { bump } = useRefresh();
  const { data, loading, error, reload } = useApi("/admin/groups");
  const { data: machines } = useApi("/admin/machines");
  const [editing, setEditing] = useState(null); // group or {new:true}
  const [name, setName] = useState("");
  const [assign, setAssign] = useState(null); // group for assignment
  const [selected, setSelected] = useState([]);

  const openNew = () => { setEditing({ new: true }); setName(""); };
  const openEdit = (g) => { setEditing(g); setName(g.name); };

  const save = async () => {
    if (!name.trim()) return;
    try {
      if (editing.new) await api.post("/admin/groups", { name });
      else await api.put(`/admin/groups/${editing.id}`, { name });
      toast.success("Group saved");
      setEditing(null); bump();
    } catch (e) { toast.error(apiError(e)); }
  };

  const archive = async (g) => {
    try { await api.post(`/admin/groups/${g.id}/archive`); toast.success("Group archived"); bump(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const openAssign = (g) => {
    setAssign(g);
    setSelected((machines || []).filter((m) => m.group_id === g.id).map((m) => m.id));
  };
  const saveAssign = async () => {
    try { await api.post(`/admin/groups/${assign.id}/assign`, { machine_ids: selected }); toast.success("Machines assigned"); setAssign(null); bump(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <PageTitle title="Machine Groups" subtitle="Organise machines into operational groups">
        <button data-testid="new-group-btn" onClick={openNew}
          className="flex items-center gap-2 rounded-sm bg-white text-black px-3 py-1.5 text-sm font-medium hover:bg-neutral-200 transition-colors">
          <Plus className="h-4 w-4" /> New Group
        </button>
      </PageTitle>

      {loading && !data ? <Loading /> : error ? <ErrorState error={error} onRetry={reload} /> :
        data.filter((g) => g.status === "active").length === 0 ? <Empty label="No active groups yet" /> : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {data.filter((g) => g.status === "active").map((g) => (
            <div key={g.id} className="rounded-sm border border-border bg-[#0f0f0f] p-4" data-testid={`group-card-${g.id}`}>
              <div className="flex justify-between items-start">
                <div>
                  <div className="font-head font-bold tracking-tight">{g.name}</div>
                  <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1"><Server className="h-3 w-3" /> {g.machine_count} machines</div>
                </div>
              </div>
              <div className="flex gap-2 mt-4">
                <button onClick={() => openAssign(g)} data-testid={`assign-group-${g.id}`}
                  className="flex-1 rounded-sm border border-border px-2 py-1.5 text-xs hover:bg-[#1c1c1c] transition-colors">Assign</button>
                <button onClick={() => openEdit(g)} className="rounded-sm border border-border px-2 py-1.5 text-xs hover:bg-[#1c1c1c] transition-colors"><Pencil className="h-3.5 w-3.5" /></button>
                <button onClick={() => archive(g)} data-testid={`archive-group-${g.id}`} className="rounded-sm border border-border px-2 py-1.5 text-xs hover:bg-[#1c1c1c] hover:text-[#EF4444] transition-colors"><Archive className="h-3.5 w-3.5" /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-[#0f0f0f] border-border" data-testid="group-dialog">
          <DialogHeader><DialogTitle className="font-head">{editing?.new ? "New Group" : "Edit Group"}</DialogTitle><DialogDescription className="sr-only">Group name form</DialogDescription></DialogHeader>
          <input data-testid="group-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Group name"
            className="w-full rounded-sm border border-input bg-[#141414] px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-white" />
          <DialogFooter>
            <button onClick={save} data-testid="save-group-btn" className="rounded-sm bg-white text-black px-4 py-2 text-sm font-medium hover:bg-neutral-200 transition-colors">Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!assign} onOpenChange={(o) => !o && setAssign(null)}>
        <DialogContent className="bg-[#0f0f0f] border-border max-h-[80vh] overflow-y-auto" data-testid="assign-dialog">
          <DialogHeader><DialogTitle className="font-head">Assign machines · {assign?.name}</DialogTitle></DialogHeader>
          <div className="space-y-1">
            {(machines || []).map((m) => (
              <label key={m.id} className="flex items-center gap-3 rounded-sm px-3 py-2 hover:bg-[#161616] cursor-pointer">
                <input type="checkbox" checked={selected.includes(m.id)}
                  onChange={(e) => setSelected((s) => e.target.checked ? [...s, m.id] : s.filter((x) => x !== m.id))} />
                <span className="mono text-sm">{m.code}</span>
                <span className="text-sm text-muted-foreground">{m.name}</span>
              </label>
            ))}
          </div>
          <DialogFooter>
            <button onClick={saveAssign} data-testid="save-assign-btn" className="rounded-sm bg-white text-black px-4 py-2 text-sm font-medium hover:bg-neutral-200 transition-colors">Save assignment</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
